// The page-1 intake form and the live results panel, shared by the office editor and iPad field capture.
import { decimalAge, scoreAssessment, type AssessmentResult } from '@ngf/calc-engine';
import { createContext, useContext, useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';

export type Num = number | null | undefined;
export type SmokingStatus = 'never' | 'current' | 'former';

/** The smoking choice; records saved before the choice existed are read from their numbers. */
export function smokingStatus(s: Measurements['smoking']): SmokingStatus | null {
  if (!s) return null;
  if (s.status) return s.status;
  const has = (v: Num) => typeof v === 'number' && v > 0;
  if (has(s.yearsQuit)) return 'former';
  if (has(s.cigsPerDay) || has(s.yearsSmoked)) return 'current';
  return s.cigsPerDay === 0 || s.yearsSmoked === 0 ? 'never' : null;
}

/** What is still missing for smoking (the server refuses "reviewed" without it): only the status. Cigarettes a day, years
 *  smoked and years since quitting are optional (Danny, 2026-10-01); the risk score counts smoking only when both cigarettes
 *  a day and years smoked are entered. */
export function smokingMissing(s: Measurements['smoking']): string[] {
  return smokingStatus(s) ? [] : ['never, current or former'];
}
/** Measurements the person refused: left empty, printed as "Refused" on the report. */
export interface Refused { bp?: boolean; height?: boolean; weight?: boolean }
export interface Stage { kp: number; rpm: number; hr: number }
export interface Measurements {
  heightIn?: Num; weightLb?: Num;
  skinfolds?: { chest?: Num; triceps?: Num; subscapular?: Num; abdomen?: Num; suprailiac?: Num; thigh?: Num } | null;
  restSbp?: Num; restDbp?: Num; restHr?: Num;
  refused?: Refused | null;
  totalChol?: Num; hdl?: Num; ldl?: Num; triglycerides?: Num; glucose?: Num;
  smoking?: { status?: SmokingStatus | null; cigsPerDay?: Num; yearsSmoked?: Num; yearsQuit?: Num } | null;
  stages?: Stage[] | null;
  peakHr?: Num; peakSbp?: Num; peakDbp?: Num; gxtTime?: string | null;
  earlyTermination?: boolean | null; earlyTerminationReason?: string | null;
  fvc?: Num; fev1?: Num;
  sleepDisorder?: boolean | null; regularExercise?: boolean | null;
  familyHistory?: boolean | null; familyHistoryNotes?: string | null;
  medications?: boolean | null; medicationNotes?: string | null;
  comments?: string | null;
  questionnaire?: Questionnaire | null;
}

/** Health questionnaire (COHSG screening form). */
export interface Questionnaire {
  medsFor?: Partial<Record<'bloodPressure' | 'diabetes' | 'kidney' | 'thyroid' | 'cholesterol' | 'liver' | 'gout', boolean | null>> | null;
  alcohol?: 'daily' | 'weekly' | 'socially' | 'rarely' | 'never' | null;
  heartDisease?: boolean | null; diabetes?: boolean | null; familyHeartDisease?: boolean | null; familyDiabetes?: boolean | null;
  heartAttackOrStroke?: boolean | null; heartAttackOrStrokeWhich?: string | null;
  breastCancerNodes?: boolean | null; breastCancerSide?: 'left' | 'right' | 'both' | null;
}


/**
 * Fields to verify against a scan (import review only). Keys are measurement paths: "weightLb", "skinfolds.chest",
 * "stages.1.kp", "smoking", ... A flagged field gets an amber outline and a "check" tag; leaving the field (after
 * looking at it) marks it checked (green). Outside the import review there is no provider and nothing changes.
 */
export interface VerifyState { flags: Record<string, string>; checked: Set<string>; onCheck: (key: string) => void }
export const VerifyContext = createContext<VerifyState | null>(null);
const LABEL_KEY: Record<string, string> = {
  Height: 'heightIn', Weight: 'weightLb', Chest: 'skinfolds.chest', Triceps: 'skinfolds.triceps', Subscapular: 'skinfolds.subscapular',
  Abdomen: 'skinfolds.abdomen', Suprailiac: 'skinfolds.suprailiac', Thigh: 'skinfolds.thigh', 'Systolic BP': 'restSbp', 'Diastolic BP': 'restDbp',
  'Resting HR': 'restHr', 'Total cholesterol': 'totalChol', HDL: 'hdl', LDL: 'ldl', Triglycerides: 'triglycerides', Glucose: 'glucose',
  'Cigarettes / day': 'smoking', 'Cigarettes / day (when smoking)': 'smoking', 'Years smoked': 'smoking', 'Years since quitting': 'smoking',
  'Peak HR': 'peakHr', 'Peak SBP': 'peakSbp', 'Peak DBP': 'peakDbp', FVC: 'fvc', FEV1: 'fev1', 'Regular exercise': 'regularExercise',
  'Sleep disorder': 'sleepDisorder', 'Family history': 'familyHistory', 'Prescription meds': 'medications', 'Early termination': 'earlyTermination',
};
function useVerify(key: string | undefined) {
  const v = useContext(VerifyContext);
  if (!v || !key || !v.flags[key]) return { cls: '', tag: null as ReactNode, onBlur: undefined as (() => void) | undefined };
  const done = v.checked.has(key);
  return {
    cls: done ? ' verify-ok' : ' verify-need',
    // The tag is a button too: clicking it marks the box checked (as leaving the box after looking at it does).
    tag: <VerifyTag done={done} why={v.flags[key]!} onCheck={() => v.onCheck(key)} /> as ReactNode,
    onBlur: () => v.onCheck(key),
  };
}
/** The "check" / "✓ checked" tag beside a field to verify against the scan. Click it to mark the field checked. */
export function VerifyTag({ done, why, onCheck }: { done: boolean; why: string; onCheck: () => void }) {
  return (
    <button type="button" className={done ? 'verify-tag ok' : 'verify-tag'} title={done ? why : `${why} (click when checked against the scan)`}
      onMouseDown={(e) => e.preventDefault()} onClick={onCheck}>{done ? '✓ checked' : 'check'}</button>);
}
function Verified({ k, children }: { k: string | undefined; children: (x: ReturnType<typeof useVerify>) => ReactNode }) {
  return <>{children(useVerify(k))}</>;
}

/**
 * A number box. The mouse wheel still changes it (only with the cursor in the box and the pointer over it, as browsers do),
 * but a change made that way is flagged: the box turns amber and a note under it offers Undo, so a value nudged by scrolling
 * past is not missed (Danny 2026-10-06). Typing or the arrow keys clear the flag.
 */
function NumberInput({ value, onText, className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string; onText: (raw: string) => void;
}) {
  const wheelAt = useRef(0);
  const [from, setFrom] = useState<string | null>(null);
  const flagged = from !== null && from !== value;
  return (<>
    <input {...rest} type="number" value={value} className={[className, flagged ? 'wheel-changed' : ''].filter(Boolean).join(' ') || undefined}
      onWheel={() => { wheelAt.current = Date.now(); }}
      onChange={(e) => {
        if (Date.now() - wheelAt.current < 600) { if (from === null) setFrom(value); } else setFrom(null);
        onText(e.target.value);
      }} />
    {flagged && <div className="wheel-note" role="status">Changed by scrolling: {from || 'blank'} → {value || 'blank'}{' '}
      <button type="button" className="link" onClick={() => { onText(from!); setFrom(null); }}>Undo</button></div>}
  </>);
}

/** One cycle-stage input, highlighted when the import review flags it. */
// Stage values use 0 for "blank". A real 0 (e.g. a stage ridden at 0 kp, Danny 2026-10-01) is kept as typed, and shown
// whenever the stage has a heart rate (showZero). A 0-kp stage is recorded but not used for VO2max (needs resistance).
function StageCell({ k, label, value, onChange, disabled, step, showZero = false }: { k: string; label: string; value: number; onChange: (v: number) => void; disabled: boolean; step?: string; showZero?: boolean }) {
  const vf = useVerify(k);
  const shown = (v: number) => (v || showZero ? String(v) : '');
  const [text, setText] = useState(() => shown(value));
  useEffect(() => { if ((Number(text) || 0) !== (value || 0)) setText(shown(value)); }, [value]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <td className={vf.cls.trim()}>
      <NumberInput aria-label={label} min="0" step={step} inputMode={step ? 'decimal' : 'numeric'} disabled={disabled} onBlur={vf.onBlur}
        value={text === '' && showZero ? '0' : text} onText={(raw) => { setText(raw); onChange(raw === '' ? 0 : Number(raw)); }} />{vf.tag}
    </td>
  );
}

const fid = (label: string) => `f-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

/** Visit types (chosen on "New assessment"): the full NGF physical, a COHSG screening, or labs only (no form). */
export type VisitType = 'physical' | 'cohsg' | 'labs';
export const VISIT_TYPE_LABEL: Record<VisitType, string> = { physical: 'Physical', cohsg: 'COHSG screening', labs: 'Labs only' };

/** The intake form. A COHSG screening shows only its own fields: the questionnaire, height / weight, blood pressure, blood work,
 *  smoking and regular exercise (no skinfolds, cycle test or spirometry). */
export function MeasurementFields({ m, update, editable, sex, prev, visitType = 'physical' }: {
  m: Measurements; update: (p: Partial<Measurements>) => void; editable: boolean; sex: 'M' | 'F' | null; prev: Measurements; visitType?: VisitType;
}) {
  const cohsg = visitType === 'cohsg';
  const rf = m.refused ?? {};
  // Refused (Tammy, 2026-10-28): the value is cleared and the report prints "Refused". Blood pressure is one tick for both numbers.
  const refuse = (k: keyof Refused, on: boolean) => {
    const next: Refused = { ...rf, [k]: on }; if (!on) delete next[k];
    const clear: Partial<Measurements> = !on ? {} : k === 'bp' ? { restSbp: null, restDbp: null } : k === 'height' ? { heightIn: null } : { weightLb: null };
    update({ ...clear, refused: Object.keys(next).length ? next : null });
  };
  const num = (label: string, value: Num, onChange: (v: number | null) => void, prevV: Num,
    opts: { step?: string; unit?: string; refused?: keyof Refused; refusedTick?: boolean } = {}) => (
    <Verified k={LABEL_KEY[label]}>{(vf) => (
    <div className={`field${vf.cls}`}>
      <label htmlFor={fid(label)}>{label}{opts.unit ? <span className="muted"> ({opts.unit})</span> : null}{vf.tag}
        {opts.refused && opts.refusedTick !== false && (
          <span className="small" style={{ float: 'right', fontWeight: 'normal' }}>
            <input type="checkbox" disabled={!editable} checked={!!rf[opts.refused]} onChange={(e) => refuse(opts.refused!, e.target.checked)}
              aria-label={`${opts.refused === 'bp' ? 'Blood pressure' : label} refused`} /> {opts.refused === 'bp' ? 'BP refused' : 'Refused'}</span>)}</label>
      <NumberInput id={fid(label)} inputMode="decimal" step={opts.step ?? 'any'} disabled={!editable || (!!opts.refused && !!rf[opts.refused])} onBlur={vf.onBlur}
        placeholder={opts.refused && rf[opts.refused] ? 'Refused' : undefined}
        value={value === null || value === undefined ? '' : String(value)} onText={(raw) => onChange(raw === '' ? null : Number(raw))} />
      {/* Always one line tall, so fields in a row line up whether or not there is a previous value. */}
      <div className="prev">{prevV !== undefined && prevV !== null ? `Previous: ${prevV}` : ' '}</div>
    </div>)}</Verified>
  );
  const yesNo = (label: string, value: boolean | null | undefined, onChange: (v: boolean | null) => void) => (
    <Verified k={LABEL_KEY[label]}>{(vf) => (
    <div className={`field${vf.cls}`}><label htmlFor={fid(label)}>{label}{vf.tag}</label>
      <select id={fid(label)} disabled={!editable} onBlur={vf.onBlur} value={value === true ? 'yes' : value === false ? 'no' : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'yes')}>
        <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
      </select></div>)}</Verified>
  );
  const sf = m.skinfolds ?? {};
  const setSf = (k: keyof NonNullable<Measurements['skinfolds']>) => (v: number | null) => update({ skinfolds: { ...sf, [k]: v } });
  const sm = m.smoking ?? {};
  const setSm = (k: 'cigsPerDay' | 'yearsSmoked' | 'yearsQuit') => (v: number | null) => update({ smoking: { ...sm, [k]: v } });
  const smStatus = smokingStatus(m.smoking);
  const chooseSmoking = (st: SmokingStatus) => update({ smoking: st === 'never'
    ? { status: 'never', cigsPerDay: 0, yearsSmoked: 0, yearsQuit: 0 }
    : { status: st, cigsPerDay: smStatus === 'never' ? null : sm.cigsPerDay ?? null, yearsSmoked: smStatus === 'never' ? null : sm.yearsSmoked ?? null,
        yearsQuit: st === 'current' ? 0 : smStatus === 'former' ? sm.yearsQuit ?? null : null } });
  const smMissing = smokingMissing(m.smoking);
  const stages: Stage[] = m.stages?.length ? m.stages : [{ kp: 0, rpm: 50, hr: 0 }, { kp: 0, rpm: 50, hr: 0 }, { kp: 0, rpm: 50, hr: 0 }];
  const setStage = (i: number, k: keyof Stage, v: number) => update({ stages: stages.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });

  return (
    <>
    {cohsg && <QuestionnaireFields q={m.questionnaire ?? null} editable={editable} onChange={(q) => update({ questionnaire: q })} open />}
    <fieldset><legend>Body</legend>
      <div className="row">
        {num('Height', m.heightIn, (v) => update({ heightIn: v }), prev.heightIn, { unit: 'in', refused: 'height' })}
        {num('Weight', m.weightLb, (v) => update({ weightLb: v }), prev.weightLb, { unit: 'lb', refused: 'weight' })}
      </div>
      {!cohsg && <>
      <label className="muted small" style={{ marginTop: '0.25rem' }}>Skinfolds (mm) — men 6 sites, women 5 (no chest)</label>
      <div className="row">
        {sex !== 'F' && num('Chest', sf.chest, setSf('chest'), prev.skinfolds?.chest)}
        {num('Triceps', sf.triceps, setSf('triceps'), prev.skinfolds?.triceps)}
        {num('Subscapular', sf.subscapular, setSf('subscapular'), prev.skinfolds?.subscapular)}
      </div>
      <div className="row">
        {num('Abdomen', sf.abdomen, setSf('abdomen'), prev.skinfolds?.abdomen)}
        {num('Suprailiac', sf.suprailiac, setSf('suprailiac'), prev.skinfolds?.suprailiac)}
        {num('Thigh', sf.thigh, setSf('thigh'), prev.skinfolds?.thigh)}
      </div></>}
    </fieldset>

    <fieldset><legend>Resting vitals</legend>
      <div className="row">
        {num('Systolic BP', m.restSbp, (v) => update({ restSbp: v }), prev.restSbp, { unit: 'mmHg', refused: 'bp' })}
        {num('Diastolic BP', m.restDbp, (v) => update({ restDbp: v }), prev.restDbp, { unit: 'mmHg', refused: 'bp', refusedTick: false })}
        {!cohsg && num('Resting HR', m.restHr, (v) => update({ restHr: v }), prev.restHr, { unit: 'bpm' })}
      </div>
    </fieldset>

    <fieldset><legend>Blood work</legend>
      <div className="row">
        {num('Total cholesterol', m.totalChol, (v) => update({ totalChol: v }), prev.totalChol)}
        {num('HDL', m.hdl, (v) => update({ hdl: v }), prev.hdl)}
        {num('LDL', m.ldl, (v) => update({ ldl: v }), prev.ldl)}
      </div>
      <div className="row">
        {num('Triglycerides', m.triglycerides, (v) => update({ triglycerides: v }), prev.triglycerides)}
        {num('Glucose', m.glucose, (v) => update({ glucose: v }), prev.glucose)}
      </div>
    </fieldset>

    <fieldset><legend>Smoking</legend>
      <Verified k="smoking">{(vf) => (
      <div className={`choice-row${vf.cls}`} role="radiogroup" aria-label="Smoking" onBlur={vf.onBlur}>{vf.tag}
        {(['never', 'current', 'former'] as const).map((st) => (
          <label key={st} className={`choice${smStatus === st ? ' on' : ''}`}>
            <input type="radio" name="smoking-status" disabled={!editable} checked={smStatus === st} onChange={() => chooseSmoking(st)} />
            {st === 'never' ? 'Never smoked' : st === 'current' ? 'Current smoker' : 'Former smoker (quit)'}
          </label>))}
        {prev.smoking && <span className="prev">Previous: {smokingStatus(prev.smoking) ?? '—'}{smokingStatus(prev.smoking) !== 'never' && prev.smoking.cigsPerDay ? `, ${prev.smoking.cigsPerDay}/day` : ''}</span>}
      </div>)}</Verified>
      {(smStatus === 'current' || smStatus === 'former') && (
        <div className="row">
          <div className="field">{num(smStatus === 'former' ? 'Cigarettes / day (when smoking)' : 'Cigarettes / day', sm.cigsPerDay, setSm('cigsPerDay'), prev.smoking?.cigsPerDay)}</div>
          <div className="field">{num('Years smoked', sm.yearsSmoked, setSm('yearsSmoked'), prev.smoking?.yearsSmoked)}</div>
          {smStatus === 'former' && <div className="field">{num('Years since quitting', sm.yearsQuit, setSm('yearsQuit'), prev.smoking?.yearsQuit)}</div>}
        </div>)}
      {editable && smMissing.length > 0 && <div className="hint warn">Required before marking reviewed: {smMissing.join(', ')}.</div>}
      {editable && (smStatus === 'current' || smStatus === 'former') && !(sm.cigsPerDay && sm.yearsSmoked) &&
        <div className="hint">Optional, but the coronary risk score counts smoking only when cigarettes a day and years smoked are both entered.</div>}
    </fieldset>

    <fieldset><legend>History</legend>
      <div className="row">
        {yesNo('Regular exercise', m.regularExercise, (v) => update({ regularExercise: v }))}
        {!cohsg && yesNo('Sleep disorder', m.sleepDisorder, (v) => update({ sleepDisorder: v }))}
        {!cohsg && yesNo('Family history', m.familyHistory, (v) => update({ familyHistory: v }))}
        {!cohsg && yesNo('Prescription meds', m.medications, (v) => update({ medications: v }))}
      </div>
      {m.familyHistory && <div className="field"><label>Family history details</label>
        <input disabled={!editable} value={m.familyHistoryNotes ?? ''} onChange={(e) => update({ familyHistoryNotes: e.target.value })} /></div>}
      {m.medications && <div className="field"><label>Medications</label>
        <input disabled={!editable} value={m.medicationNotes ?? ''} onChange={(e) => update({ medicationNotes: e.target.value })} /></div>}
    </fieldset>

    {!cohsg && <>
    {/* Physical: no COHSG questionnaire (Danny, 2026-10-01); shown only if an older record already has answers. */}
    {m.questionnaire && <QuestionnaireFields q={m.questionnaire} editable={editable} onChange={(q) => update({ questionnaire: q })} />}

    <fieldset><legend>Cycle ergometer (ACSM)</legend>
      <table className="stages">
        <thead><tr><th>Stage</th><th>Resistance (kp)</th><th>RPM</th><th>Heart rate</th><th className="muted">Previous</th></tr></thead>
        <tbody>{stages.map((s, i) => (
          <tr key={i}><td>{i === 0 ? '2 min' : i === 1 ? '4 min' : i === 2 ? '6 min' : `Stage ${i + 1}`}</td>
            <StageCell k={`stages.${i}.kp`} label={`Stage ${i + 1} resistance (kp)`} step="0.5" disabled={!editable} value={s.kp} showZero={s.hr > 0} onChange={(v) => setStage(i, 'kp', v)} />
            <StageCell k={`stages.${i}.rpm`} label={`Stage ${i + 1} RPM`} disabled={!editable} value={s.rpm} onChange={(v) => setStage(i, 'rpm', v)} />
            <StageCell k={`stages.${i}.hr`} label={`Stage ${i + 1} heart rate`} disabled={!editable} value={s.hr} onChange={(v) => setStage(i, 'hr', v)} />
            <td className="muted small">{prev.stages?.[i] ? `${prev.stages[i]!.kp} kp · ${prev.stages[i]!.hr} bpm` : ''}</td></tr>))}</tbody>
      </table>
      <div className="row" style={{ marginTop: '0.75rem' }}>
        {num('Peak HR', m.peakHr, (v) => update({ peakHr: v }), prev.peakHr)}
        {num('Peak SBP', m.peakSbp, (v) => update({ peakSbp: v }), prev.peakSbp)}
        {num('Peak DBP', m.peakDbp, (v) => update({ peakDbp: v }), prev.peakDbp)}
        <div className="field"><label htmlFor="f-gxt">GXT time</label><input id="f-gxt" disabled={!editable} placeholder="m:ss" value={m.gxtTime ?? ''} onChange={(e) => update({ gxtTime: e.target.value })} /></div>
      </div>
      <div className="row">
        {yesNo('Early termination', m.earlyTermination, (v) => update({ earlyTermination: v }))}
        {m.earlyTermination && <div className="field" style={{ flex: '3 1 300px' }}><label>Reason</label>
          <input disabled={!editable} value={m.earlyTerminationReason ?? ''} onChange={(e) => update({ earlyTerminationReason: e.target.value })} /></div>}
      </div>
    </fieldset>

    <fieldset><legend>Spirometry</legend>
      <div className="row">
        {num('FVC', m.fvc, (v) => update({ fvc: v }), prev.fvc, { unit: 'L', step: '0.01' })}
        {num('FEV1', m.fev1, (v) => update({ fev1: v }), prev.fev1, { unit: 'L', step: '0.01' })}
      </div>
    </fieldset>
    </>}

    <fieldset><legend>Comments <span className="muted small">(staff only; not printed on the report)</span></legend>
      <textarea aria-label="Comments" rows={4} disabled={!editable} value={m.comments ?? ''} onChange={(e) => update({ comments: e.target.value })} />
    </fieldset>
    </>
  );
}

/** Drop empty sections so stored JSON stays tidy. */
export function clean(m: Measurements): Measurements {
  const out: Measurements = { ...m };
  if (out.stages) out.stages = out.stages.filter((s) => s.kp > 0 || s.hr > 0);
  if (out.stages && !out.stages.length) delete out.stages;
  return out;
}

/** "12 wk" for a goal date offset in days. */
const weeks = (days: number) => (days <= 0 ? 'now' : `~${Math.max(1, Math.round(days / 7))} wk`);
const f1 = (x: number | null | undefined) => (x === null || x === undefined ? '—' : x.toFixed(1));
/** Fixed decimals, or a dash: imported ePhysioLogic results can lack any value. */
const fx = (x: number | null | undefined, d: number, unit = '') => (x === null || x === undefined ? '—' : `${x.toFixed(d)}${unit}`);
/** Percent with one decimal; blank when absent (legacy records may lack a value). */
const pct = (x: number | null | undefined) => (x === null || x === undefined ? '' : `${x.toFixed(1)}%`);

/** Live / issued results. A COHSG screening shows only coronary risk (it has no cycle test, skinfolds or spirometry). */
export function Results({ live, prev, prevDate, label, visitType = 'physical' }: { live: AssessmentResult | null; prev: AssessmentResult | null; prevDate?: string; label: string; visitType?: VisitType }) {
  const full = visitType !== 'cohsg';
  const row = (k: string, v: ReactNode, p: ReactNode = '') => (
    <><div className="k">{k}</div><div className="v">{v}</div><div className="p">{p}</div></>
  );
  const hdr = <><div /><div className="hdr" style={{ textAlign: 'right' }}>Now</div><div className="hdr" style={{ textAlign: 'right' }}>{prevDate ? prevDate.slice(0, 4) : ''}</div></>;
  if (!live) return <div className="card"><h2>Results</h2><p className="muted">Enter measurements to see results.</p></div>;
  const a = live.aerobic, b = live.bodyComposition, c = live.coronaryRisk, s = live.spirometry;
  const pa = prev?.aerobic, pb = prev?.bodyComposition, pc = prev?.coronaryRisk, ps = prev?.spirometry;
  return (
    <div className="card">
      <div className="spread"><h2 style={{ margin: 0 }}>Results</h2><span className="muted small">{label}</span></div>
      {live.provisional.length > 0 && <p className="small"><span className="badge provisional">provisional</span> {live.provisional.join(', ')}</p>}

      {full && <>
      <h3 style={{ marginTop: '0.75rem' }}>Aerobic capacity</h3>
      {a ? <div className="kv">{hdr}
        {row('VO2max (ml/kg/min)', a.vo2maxDisplayed, pa?.vo2maxDisplayed)}
        {row('Class', a.aerobicClass, pa?.aerobicClass)}
        {row('% of age average', `${a.pctOfAverage}%`, pa ? `${pa.pctOfAverage}%` : '')}
        {row('Age norm', a.ageNorm)}
        {row('Max HR', a.maxHr)}
        {row('Training HR', a.targetHr ? `${a.targetHr.low}–${a.targetHr.high}` : '—', pa?.targetHr ? `${pa.targetHr.low}–${pa.targetHr.high}` : '')}
        {row('METs', a.mets, pa?.mets)}
        {row('Achievable VO2', a.achievableVo2 ?? '—')}
      </div> : <p className="muted small">Weight and at least two cycle stages.</p>}

      <h3 style={{ marginTop: '0.75rem' }}>Body composition</h3>
      {b ? <div className="kv">{hdr}
        {row('Body fat', `${f1(b.fatPct)}%`, pb ? `${f1(pb.fatPct)}%` : '')}
        {row('Lean weight (lb)', f1(b.leanWeightLb), pb ? f1(pb.leanWeightLb) : '')}
        {row('Fat weight (lb)', f1(b.fatWeightLb), pb ? f1(pb.fatWeightLb) : '')}
        {/* ePhysioLogic records did not store the target range (null on imported history). */}
        {row('Target range', b.targetRange ? `${b.targetRange[0]}–${b.targetRange[1]}%` : '—')}
        {row('Target weight (lb)', f1(b.targetWeightLb))}
        {b.goals !== undefined && (b.goals
          ? <>{row('Short-term goal', `${f1(b.goals.shortTerm.fatPct)}% · ${f1(b.goals.shortTerm.weightLb)} lb`, `${weeks(b.goals.shortTerm.days)}`)}
              {row('Target goal', `${f1(b.goals.target.fatPct)}% · ${f1(b.goals.target.weightLb)} lb`, `${weeks(b.goals.target.days)}`)}</>
          : row('Goal', 'At or below target — maintain'))}
      </div> : <p className="muted small">Weight and all skinfold sites.</p>}
      </>}

      <h3 style={{ marginTop: '0.75rem' }}>Coronary risk</h3>
      {c ? <div className="kv">{hdr}
        {row('Score', c.score, pc?.score)}
        {row('Class', c.riskClass, pc?.riskClass)}
        {row('SBP percentile', c.percentiles.sbp, pc?.percentiles.sbp)}
        {row('DBP percentile', c.percentiles.dbp, pc?.percentiles.dbp)}
        {row('Cholesterol pct.', c.percentiles.totalChol, pc?.percentiles.totalChol)}
        {row('HDL percentile', c.percentiles.hdl, pc?.percentiles.hdl)}
        {row('Smoking pct.', c.percentiles.smoking, pc?.percentiles.smoking)}
        {live.cholHdlRatio != null && row('Chol / HDL ratio', live.cholHdlRatio.toFixed(1), prev?.cholHdlRatio != null ? prev.cholHdlRatio.toFixed(1) : '')}
      </div> : live.cholHdlRatio != null
        ? <div className="kv">{hdr}{row('Chol / HDL ratio', live.cholHdlRatio.toFixed(1), prev?.cholHdlRatio != null ? prev.cholHdlRatio.toFixed(1) : '')}</div>
        : <p className="muted small">BP, total cholesterol, HDL and smoking.</p>}

      {full && <>
      <h3 style={{ marginTop: '0.75rem' }}>Spirometry</h3>
      {s ? <div className="kv">{hdr}
        {row('FVC predicted (L)', fx(s.fvcPredicted, 2), ps?.fvcPredicted?.toFixed(2))}
        {row('FVC % predicted', fx(s.fvcPctPredicted, 1, '%'), pct(ps?.fvcPctPredicted))}
        {row('FEV1 predicted (L)', fx(s.fev1Predicted, 2), ps?.fev1Predicted?.toFixed(2))}
        {row('FEV1 % predicted', fx(s.fev1PctPredicted, 1, '%'), pct(ps?.fev1PctPredicted))}
        {row('FEV1/FVC', fx(s.fev1FvcPct, 1, '%'), pct(ps?.fev1FvcPct))}
        {row('Interpretation', s.obstruction, ps?.obstruction)}
      </div> : <p className="muted small">Height, FVC and FEV1.</p>}
      </>}
    </div>
  );
}


/** Score in the browser with the same calc engine the server stores results with (works offline). */
export function scoreLive(sex: 'M' | 'F' | null, dob: string | null, assessedOn: string, m: Measurements) {
  if (!sex || !dob || !assessedOn) return null;
  const age = decimalAge(dob, assessedOn);
  const s = m.smoking;
  return {
    age,
    r: scoreAssessment({
      sex, age, heightIn: m.heightIn ?? null, weightLb: m.weightLb ?? null, skinfolds: m.skinfolds ?? null,
      sbp: m.restSbp ?? null, dbp: m.restDbp ?? null, totalChol: m.totalChol ?? null, hdl: m.hdl ?? null,
      smoking: s ? { cigsPerDay: s.cigsPerDay ?? 0, yearsSmoked: s.yearsSmoked ?? 0, yearsQuit: s.yearsQuit ?? 0 } : null,
      restHr: m.restHr ?? null, stages: m.stages?.filter((x) => x.kp > 0 && x.hr > 0) ?? null,
      fvc: m.fvc ?? null, fev1: m.fev1 ?? null,
    }),
  };
}

const MEDS: [keyof NonNullable<Questionnaire['medsFor']>, string][] = [
  ['bloodPressure', 'High blood pressure'], ['diabetes', 'Diabetes'], ['kidney', 'Kidney'], ['thyroid', 'Thyroid'],
  ['cholesterol', 'High cholesterol'], ['liver', 'Liver'], ['gout', 'Gout'],
];

/** COHSG health questionnaire: collapsed unless answers exist. Not used by the scores; shown to the physician. */
function QuestionnaireFields({ q, editable, onChange, open = false }: { q: Questionnaire | null; editable: boolean; onChange: (q: Questionnaire | null) => void; open?: boolean }) {
  const cur = q ?? {};
  const set = (p: Partial<Questionnaire>) => onChange({ ...cur, ...p });
  const yn = (label: string, value: boolean | null | undefined, apply: (v: boolean | null) => void) => (
    <div className="field"><label>{label}</label>
      <select disabled={!editable} value={value === true ? 'yes' : value === false ? 'no' : ''} onChange={(e) => apply(e.target.value === '' ? null : e.target.value === 'yes')}>
        <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
      </select></div>);
  return (
    <details className="questionnaire" open={open || !!q}>
      <summary><b>Health questionnaire (COHSG)</b> <span className="muted small">{q ? '' : 'none recorded'}</span></summary>
      <fieldset>
        <legend>Takes medication for</legend>
        <div className="row">{MEDS.map(([k, label]) => <div key={k}>{yn(label, cur.medsFor?.[k], (v) => set({ medsFor: { ...(cur.medsFor ?? {}), [k]: v } }))}</div>)}</div>
        <div className="row">
          <div className="field"><label>Alcohol</label>
            <select disabled={!editable} value={cur.alcohol ?? ''} onChange={(e) => set({ alcohol: (e.target.value || null) as Questionnaire['alcohol'] })}>
              <option value="">—</option>{['daily', 'weekly', 'socially', 'rarely', 'never'].map((v) => <option key={v} value={v}>{v[0]!.toUpperCase() + v.slice(1)}</option>)}
            </select></div>
          {yn('Diagnosed heart disease', cur.heartDisease, (v) => set({ heartDisease: v }))}
          {yn('Diagnosed diabetes', cur.diabetes, (v) => set({ diabetes: v }))}
          {yn('Family history: heart disease', cur.familyHeartDisease, (v) => set({ familyHeartDisease: v }))}
          {yn('Family history: diabetes', cur.familyDiabetes, (v) => set({ familyDiabetes: v }))}
        </div>
        <div className="row">
          {yn('Heart attack or stroke', cur.heartAttackOrStroke, (v) => set({ heartAttackOrStroke: v }))}
          {cur.heartAttackOrStroke && <div className="field"><label>Which</label>
            <input disabled={!editable} value={cur.heartAttackOrStrokeWhich ?? ''} onChange={(e) => set({ heartAttackOrStrokeWhich: e.target.value || null })} /></div>}
          {yn('Breast cancer, lymph nodes removed', cur.breastCancerNodes, (v) => set({ breastCancerNodes: v }))}
          {cur.breastCancerNodes && <div className="field"><label>Side</label>
            <select disabled={!editable} value={cur.breastCancerSide ?? ''} onChange={(e) => set({ breastCancerSide: (e.target.value || null) as Questionnaire['breastCancerSide'] })}>
              <option value="">—</option><option value="left">Left</option><option value="right">Right</option><option value="both">Both</option>
            </select></div>}
        </div>
        {cur.breastCancerNodes && <p className="hint warn">Lymph nodes removed: take blood pressure on the other arm.</p>}
      </fieldset>
    </details>
  );
}
